/**
 * 地域別最低賃金マスターデータ（令和5年度〜令和7年度）
 *
 * 出典：
 *   - 厚生労働省「平成14年度から令和7年度までの地域別最低賃金改定状況」
 *     https://www.mhlw.go.jp/content/11200000/001571219.xlsx
 *   - 厚生労働省「令和7年度地域別最低賃金 全国一覧」
 *     https://www.mhlw.go.jp/content/11200000/001571192.pdf
 *
 * 最終更新：2026-05-03
 *
 * 注意：
 *   - effective は ISO 日付（YYYY-MM-DD）。発効日が都道府県により異なる。
 *   - 秋田県の R7 発効は 2026-03-31。AI導入補助金 申請開始（2026-03-30）時点ではまだ R6 が法定値。
 *   - 加点措置②（R7.7+63円）の判定用 R7.7時点値は R6.amount と一致するため、別データは不要。
 */

export type WageEntry = {
  amount: number; // 円/時間
  effective: string; // ISO 日付 YYYY-MM-DD
};

export type PrefectureWageHistory = {
  no: number;
  name: string;
  R5: WageEntry; // 令和5年度（2023年改定）
  R6: WageEntry; // 令和6年度（2024年改定）= R7.7時点で適用
  R7: WageEntry; // 令和7年度（2025年改定）= 申請時点（2026年）の現行値
};

export const MIN_WAGES: readonly PrefectureWageHistory[] = [
  { no:  1, name: '北海道',   R5: { amount:  960, effective: '2023-10-01' }, R6: { amount: 1010, effective: '2024-10-01' }, R7: { amount: 1075, effective: '2025-10-04' } },
  { no:  2, name: '青森県',   R5: { amount:  898, effective: '2023-10-07' }, R6: { amount:  953, effective: '2024-10-05' }, R7: { amount: 1029, effective: '2025-11-21' } },
  { no:  3, name: '岩手県',   R5: { amount:  893, effective: '2023-10-04' }, R6: { amount:  952, effective: '2024-10-27' }, R7: { amount: 1031, effective: '2025-12-01' } },
  { no:  4, name: '宮城県',   R5: { amount:  923, effective: '2023-10-01' }, R6: { amount:  973, effective: '2024-10-01' }, R7: { amount: 1038, effective: '2025-10-04' } },
  { no:  5, name: '秋田県',   R5: { amount:  897, effective: '2023-10-01' }, R6: { amount:  951, effective: '2024-10-01' }, R7: { amount: 1031, effective: '2026-03-31' } },
  { no:  6, name: '山形県',   R5: { amount:  900, effective: '2023-10-14' }, R6: { amount:  955, effective: '2024-10-19' }, R7: { amount: 1032, effective: '2025-12-23' } },
  { no:  7, name: '福島県',   R5: { amount:  900, effective: '2023-10-01' }, R6: { amount:  955, effective: '2024-10-05' }, R7: { amount: 1033, effective: '2026-01-01' } },
  { no:  8, name: '茨城県',   R5: { amount:  953, effective: '2023-10-01' }, R6: { amount: 1005, effective: '2024-10-01' }, R7: { amount: 1074, effective: '2025-10-12' } },
  { no:  9, name: '栃木県',   R5: { amount:  954, effective: '2023-10-01' }, R6: { amount: 1004, effective: '2024-10-01' }, R7: { amount: 1068, effective: '2025-10-01' } },
  { no: 10, name: '群馬県',   R5: { amount:  935, effective: '2023-10-05' }, R6: { amount:  985, effective: '2024-10-04' }, R7: { amount: 1063, effective: '2026-03-01' } },
  { no: 11, name: '埼玉県',   R5: { amount: 1028, effective: '2023-10-01' }, R6: { amount: 1078, effective: '2024-10-01' }, R7: { amount: 1141, effective: '2025-11-01' } },
  { no: 12, name: '千葉県',   R5: { amount: 1026, effective: '2023-10-01' }, R6: { amount: 1076, effective: '2024-10-01' }, R7: { amount: 1140, effective: '2025-10-03' } },
  { no: 13, name: '東京都',   R5: { amount: 1113, effective: '2023-10-01' }, R6: { amount: 1163, effective: '2024-10-01' }, R7: { amount: 1226, effective: '2025-10-03' } },
  { no: 14, name: '神奈川県', R5: { amount: 1112, effective: '2023-10-01' }, R6: { amount: 1162, effective: '2024-10-01' }, R7: { amount: 1225, effective: '2025-10-04' } },
  { no: 15, name: '新潟県',   R5: { amount:  931, effective: '2023-10-01' }, R6: { amount:  985, effective: '2024-10-01' }, R7: { amount: 1050, effective: '2025-10-02' } },
  { no: 16, name: '富山県',   R5: { amount:  948, effective: '2023-10-01' }, R6: { amount:  998, effective: '2024-10-01' }, R7: { amount: 1062, effective: '2025-10-12' } },
  { no: 17, name: '石川県',   R5: { amount:  933, effective: '2023-10-08' }, R6: { amount:  984, effective: '2024-10-05' }, R7: { amount: 1054, effective: '2025-10-08' } },
  { no: 18, name: '福井県',   R5: { amount:  931, effective: '2023-10-01' }, R6: { amount:  984, effective: '2024-10-05' }, R7: { amount: 1053, effective: '2025-10-08' } },
  { no: 19, name: '山梨県',   R5: { amount:  938, effective: '2023-10-01' }, R6: { amount:  988, effective: '2024-10-01' }, R7: { amount: 1052, effective: '2025-12-01' } },
  { no: 20, name: '長野県',   R5: { amount:  948, effective: '2023-10-01' }, R6: { amount:  998, effective: '2024-10-01' }, R7: { amount: 1061, effective: '2025-10-03' } },
  { no: 21, name: '岐阜県',   R5: { amount:  950, effective: '2023-10-01' }, R6: { amount: 1001, effective: '2024-10-01' }, R7: { amount: 1065, effective: '2025-10-18' } },
  { no: 22, name: '静岡県',   R5: { amount:  984, effective: '2023-10-01' }, R6: { amount: 1034, effective: '2024-10-01' }, R7: { amount: 1097, effective: '2025-11-01' } },
  { no: 23, name: '愛知県',   R5: { amount: 1027, effective: '2023-10-01' }, R6: { amount: 1077, effective: '2024-10-01' }, R7: { amount: 1140, effective: '2025-10-18' } },
  { no: 24, name: '三重県',   R5: { amount:  973, effective: '2023-10-01' }, R6: { amount: 1023, effective: '2024-10-01' }, R7: { amount: 1087, effective: '2025-11-21' } },
  { no: 25, name: '滋賀県',   R5: { amount:  967, effective: '2023-10-01' }, R6: { amount: 1017, effective: '2024-10-01' }, R7: { amount: 1080, effective: '2025-10-05' } },
  { no: 26, name: '京都府',   R5: { amount: 1008, effective: '2023-10-06' }, R6: { amount: 1058, effective: '2024-10-01' }, R7: { amount: 1122, effective: '2025-11-21' } },
  { no: 27, name: '大阪府',   R5: { amount: 1064, effective: '2023-10-01' }, R6: { amount: 1114, effective: '2024-10-01' }, R7: { amount: 1177, effective: '2025-10-16' } },
  { no: 28, name: '兵庫県',   R5: { amount: 1001, effective: '2023-10-01' }, R6: { amount: 1052, effective: '2024-10-01' }, R7: { amount: 1116, effective: '2025-10-04' } },
  { no: 29, name: '奈良県',   R5: { amount:  936, effective: '2023-10-01' }, R6: { amount:  986, effective: '2024-10-01' }, R7: { amount: 1051, effective: '2025-11-16' } },
  { no: 30, name: '和歌山県', R5: { amount:  929, effective: '2023-10-01' }, R6: { amount:  980, effective: '2024-10-01' }, R7: { amount: 1045, effective: '2025-11-01' } },
  { no: 31, name: '鳥取県',   R5: { amount:  900, effective: '2023-10-05' }, R6: { amount:  957, effective: '2024-10-05' }, R7: { amount: 1030, effective: '2025-10-04' } },
  { no: 32, name: '島根県',   R5: { amount:  904, effective: '2023-10-06' }, R6: { amount:  962, effective: '2024-10-12' }, R7: { amount: 1033, effective: '2025-11-17' } },
  { no: 33, name: '岡山県',   R5: { amount:  932, effective: '2023-10-01' }, R6: { amount:  982, effective: '2024-10-02' }, R7: { amount: 1047, effective: '2025-12-01' } },
  { no: 34, name: '広島県',   R5: { amount:  970, effective: '2023-10-01' }, R6: { amount: 1020, effective: '2024-10-01' }, R7: { amount: 1085, effective: '2025-11-01' } },
  { no: 35, name: '山口県',   R5: { amount:  928, effective: '2023-10-01' }, R6: { amount:  979, effective: '2024-10-01' }, R7: { amount: 1043, effective: '2025-10-16' } },
  { no: 36, name: '徳島県',   R5: { amount:  896, effective: '2023-10-01' }, R6: { amount:  980, effective: '2024-11-01' }, R7: { amount: 1046, effective: '2026-01-01' } },
  { no: 37, name: '香川県',   R5: { amount:  918, effective: '2023-10-01' }, R6: { amount:  970, effective: '2024-10-02' }, R7: { amount: 1036, effective: '2025-10-18' } },
  { no: 38, name: '愛媛県',   R5: { amount:  897, effective: '2023-10-06' }, R6: { amount:  956, effective: '2024-10-13' }, R7: { amount: 1033, effective: '2025-12-01' } },
  { no: 39, name: '高知県',   R5: { amount:  897, effective: '2023-10-08' }, R6: { amount:  952, effective: '2024-10-09' }, R7: { amount: 1023, effective: '2025-12-01' } },
  { no: 40, name: '福岡県',   R5: { amount:  941, effective: '2023-10-06' }, R6: { amount:  992, effective: '2024-10-05' }, R7: { amount: 1057, effective: '2025-11-16' } },
  { no: 41, name: '佐賀県',   R5: { amount:  900, effective: '2023-10-14' }, R6: { amount:  956, effective: '2024-10-17' }, R7: { amount: 1030, effective: '2025-11-21' } },
  { no: 42, name: '長崎県',   R5: { amount:  898, effective: '2023-10-13' }, R6: { amount:  953, effective: '2024-10-12' }, R7: { amount: 1031, effective: '2025-12-01' } },
  { no: 43, name: '熊本県',   R5: { amount:  898, effective: '2023-10-08' }, R6: { amount:  952, effective: '2024-10-05' }, R7: { amount: 1034, effective: '2026-01-01' } },
  { no: 44, name: '大分県',   R5: { amount:  899, effective: '2023-10-06' }, R6: { amount:  954, effective: '2024-10-05' }, R7: { amount: 1035, effective: '2026-01-01' } },
  { no: 45, name: '宮崎県',   R5: { amount:  897, effective: '2023-10-06' }, R6: { amount:  952, effective: '2024-10-05' }, R7: { amount: 1023, effective: '2025-11-16' } },
  { no: 46, name: '鹿児島県', R5: { amount:  897, effective: '2023-10-06' }, R6: { amount:  953, effective: '2024-10-05' }, R7: { amount: 1026, effective: '2025-11-01' } },
  { no: 47, name: '沖縄県',   R5: { amount:  896, effective: '2023-10-08' }, R6: { amount:  952, effective: '2024-10-09' }, R7: { amount: 1023, effective: '2025-12-01' } },
];

export type AppliedYear = 'R5' | 'R6' | 'R7';

export type CurrentMinWage = {
  amount: number;
  appliedYear: AppliedYear;
  effective: string;
};

/**
 * 申請日（あるいは判定基準日）に対し、各都道府県で「法令上の地域別最低賃金」となる額を返す。
 * targetDate 当日の発効分は適用扱い（targetDate >= effective）。
 *
 * @param prefectureName 都道府県名（"大阪府" 等）
 * @param targetDate 判定基準日（既定：今日 00:00:00 ローカル）
 */
export function getCurrentMinWage(
  prefectureName: string,
  targetDate: Date = new Date()
): CurrentMinWage {
  const entry = MIN_WAGES.find((p) => p.name === prefectureName);
  if (!entry) throw new Error(`Unknown prefecture: ${prefectureName}`);

  const r7 = new Date(entry.R7.effective);
  const r6 = new Date(entry.R6.effective);

  if (targetDate >= r7) return { amount: entry.R7.amount, appliedYear: 'R7', effective: entry.R7.effective };
  if (targetDate >= r6) return { amount: entry.R6.amount, appliedYear: 'R6', effective: entry.R6.effective };
  return { amount: entry.R5.amount, appliedYear: 'R5', effective: entry.R5.effective };
}

/**
 * 加点措置②用：令和7年7月時点（= R6改定後）の地域別最低賃金。
 * 事業者の R7.7 事業場内最低賃金の参考値として使用。
 */
export function getR77BaselineMinWage(prefectureName: string): number {
  const entry = MIN_WAGES.find((p) => p.name === prefectureName);
  if (!entry) throw new Error(`Unknown prefecture: ${prefectureName}`);
  return entry.R6.amount;
}

/**
 * 加点措置①用：R6.10〜R7.9 期間中の地域別最低賃金境界値。
 * lower = 当該期間の地域別最低賃金（= R6改定後）
 * upper = 令和7年度改定の地域別最低賃金（= R7改定後）
 */
export function getKaten1Boundaries(prefectureName: string): {
  lower: number;
  upper: number;
} {
  const entry = MIN_WAGES.find((p) => p.name === prefectureName);
  if (!entry) throw new Error(`Unknown prefecture: ${prefectureName}`);
  return { lower: entry.R6.amount, upper: entry.R7.amount };
}
